import axios from 'axios';
import type { DateTime } from 'luxon';

export default class ProductsService
{
    public static async getList(filter: FilterModel, pager: Pager)
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(`admin/store/products`, {
            params: { ...filter, ...pager.data() }
        })).data;
    }

    public static async fetch(id: number)
    {
        return (await axios.get<Resource<FormModel>>(`admin/store/products/${id}`)).data;
    }

    public static async create(model: FormModel)
    {
        return (await axios.post(`admin/store/products`, model)).data as ResponseModel;
    }

    public static async update(id: number, model: FormModel)
    {
        return (await axios.put(`admin/store/products/${id}`, model)).data as ResponseModel;
    }

    public static async remove(id: number)
    {
        return (await axios.delete(`admin/store/products/${id}`)).data as Statement;
    }

    public static async copy(id: number)
    {
        return (await axios.post(`admin/store/products/${id}/copy`)).data as Statement;
    }

    public static async getAutocompleteList(search: string, limit: number = 10, excluded: number[] = [])
    {
        return (await axios.get<AutocompleteListModel[]>('admin/store/products/autocomplete', {
            params: {
                search: search,
                limit: limit,
                excluded: excluded
            }
        })).data;
    }

    public static async fetchAutocompleteModel(id: number)
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/products/autocomplete/${id}`)).data;
    }

    public static async massActivate(model: MassActivateModel)
    {
        return (await axios.put<Statement>(`admin/store/products/mass-activate`, model)).data;
    }

    public static async massDelete(productsIds: number[])
    {
        const result = await axios.delete(`admin/store/products/mass-delete`, { data: productsIds });

        return result.data as Statement;
    }

    public static async massAddCategory(model: MassAddCategoryModel)
    {
        return (await axios.put<Statement>(`admin/store/products/mass-category-assign`, model)).data;
    }

    public static async massAddLabel(model: MassAddLabelModel)
    {
        return (await axios.put<Statement>(`admin/store/products/mass-add-label`, model)).data;
    }

    public static async massAddProducer(model: MassAddProducerModel)
    {
        return (await axios.put<Statement>(`admin/store/products/mass-add-producer`, model)).data;
    }

    public static async massAddCollection(model: MassAddCollectionModel)
    {
        return (await axios.put<Statement>(`admin/store/products/mass-collection-assign`, model)).data;
    }

    public static async getPropertiesList(categoriesIds: number[], isVariant: boolean)
    {
        return (await axios.get<CategoryPropertiesModel[]>(`admin/store/products/properties`, {
            params: {
                categoriesIds: categoriesIds,
                isVariant: isVariant
            }
        })).data;
    }

    public static async getListByIds(productsIds: number[])
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/products/ids`, {
            params: {
                productsIds: productsIds
            }
        })).data;
    }

    public static async getVariantsList(id: number, filter: FilterModel, pager: Pager)
    {
        return (await axios.get<Pagination<Resource<VariantListItemModel>>>(`admin/store/products/${id}/variants`, {
            params: { ...filter, ...pager.data() }
        })).data;
    }

    public static async createVariants(id: number, model: VariantsFormModel)
    {
        return (await axios.post<Pagination<Resource<ListItemModel>>>(`admin/store/products/${id}/variants`, model)).data as Statement;
    }

    public static async getVariantsProperties(id: number)
    {
        return (await axios.get<VariantsFormModel>(`admin/store/products/${id}/variants-properties`)).data;
    }
}

export enum MassActions
{
    Activate = 'activate',
    Delete = 'delete',
    AddCategory = 'addCategory',
    AddLabel = 'addLabel',
    AddProducer = 'addProducer',
    AddCollection = 'addCollection'
}

export interface MassActivateModel
{
    productsIds: number[];
    activate: boolean;
}

export interface MassAddCategoryModel
{
    productsIds: number[];
    categoryId: number;
    isMain: boolean;
}

export interface MassAddLabelModel
{
    productsIds: number[];
    labelId: number;
}

export interface MassAddProducerModel
{
    productsIds: number[];
    producerId: number;
}

export interface MassAddCollectionModel
{
    productsIds: number[];
    collectionId: number;
}

export interface PropertyModel
{
    id: number;
    name: string;
    isNumerical: boolean;
    isRequired: boolean;
}

export interface PropertyValueDataModel
{
    id?: number;
    stringValue: Record<string, string>;
    numericValue?: number;
}

export interface CategoryPropertiesModel
{
    id: number;
    name: string;
    properties: PropertyModel[];
}

export interface ListItemModel
{
    id: number;
    isActive: boolean;
    hasVariants: boolean;
    picture: FileResource;
    name: string;
    producer: string;
    ean13: string;
    storageCount: number;
    dateModifiedUtc?: DateTime;
    createdBy: string;
}

export interface FormModel
{
    id: number;
    name: string;
    isActive: boolean;
    hasVariants: boolean;
    displayName: Record<string, string>;
    description: Record<string, string>;
    summary: Record<string, string>;
    keywords: Record<string, string>;
    ean13: string;
    sku: string;
    mpn: string;
    storageCount: number;
    isInStorage: boolean;
    minStorageCount?: number;
    maxStorageCount?: number;
    hasWeight: false;
    weight: number;
    productionCountry: string;
    mainCategoryId?: number;
    taxationId?: number;
    producerId?: number;
    categories: number[];
    photos: File[];
    attachments: File[];
    packageWidth?: number;
    packageHeight?: number;
    packageDepth?: number;
    packageWeight?: number;
    shippingOnPallet: boolean;
    interestingProducts: number[];
    recommendedProducts: number[];
    complementaryProducts: number[];
    labels: number[];
    basePrices: Record<number, ProductPriceModel>;
    properties: Record<number, PropertyValueDataModel>;
    collections: number[];
    baseProductId?: number;
    isRecommended: boolean;
    dateRecommendationStartUtc?: DateTime;
    dateRecommendationEndUtc?: DateTime;
    baseProductInheritanceSettings: Record<string, boolean>;
}

export interface ProductPackageSizeModel
{
    width: number;
    height: number;
    depth: number;
    weight: number;
}

export interface ProductPriceModel
{
    priceGross: number;
    priceNet: number;
}

export interface FilterModel
{
    name: string;
    isActive?: boolean;
    storageCountFrom?: number;
    storageCountDue?: number;
    producerId?: number;
    currencyId?: number;
    priceGrossFrom?: number;
    priceGrossDue?: number;
    priceNetFrom?: number;
    priceNetDue?: number;
    labelId?: number;
    hasPicture?: boolean;
    categoryId?: number;
    createdBy?: number;
    priceFrom?: number;
    priceTo?: number;
    manufacturerId?: number;
    dateCreatedFromUtc?: DateTime;
    dateCreatedDueUtc?: DateTime;
    withVariants: boolean;
    dateModifiedFromUtc?: DateTime;
    dateModifiedDueUtc?: DateTime;
}

export interface AutocompleteListModel
{
    id: number;
    name: string;
    ean13: string;
}

export interface ResponseModel
{
    id: number;
}

export interface VariantsFormModel
{
    properties: Record<number, number[]>;
    variants: PropertyValueModel[][];
}

export interface PropertyValueModel
{
    propertyId: number;
    valueId: number;
}

export interface VariantsFilterModel
{
    isActive?: boolean;
}

export interface VariantListItemModel
{
    id: number;
    isActive: boolean;
    name: string;
}
